import React from 'react';
import ReactMarkdown from 'react-markdown';

const markdownComponentsLarge = {
  h1: ({ children }) => (
    <h1 className="text-3xl font-bold mb-6 text-white border-b border-gray-600 pb-2">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-xl font-semibold mb-4 mt-8 text-white">{children}</h2>
  ),
  p: ({ children }) => <p className="text-gray-300 mb-4 leading-relaxed">{children}</p>,
  ul: ({ children }) => (
    <ul className="list-disc list-inside text-gray-300 mb-4 ml-4 space-y-1">{children}</ul>
  ),
  li: ({ children }) => <li className="text-gray-300">{children}</li>,
  a: ({ href, children }) => (
    <a href={href} className="text-white hover:underline">
      {children}
    </a>
  ),
};

const SettingsModal = ({
  isOpen,
  page,
  onClose,
  onNavigate,
  usageStats,
  freemiumLimits,
  privacyContent,
  termsContent,
}) => {
  if (!isOpen) return null;

  const navButtonClass = target =>
    `px-6 py-3 rounded-lg font-semibold transition-colors ${
      page === target || (!page && target === 'overview')
        ? 'bg-[#f5f5f0] text-gray-800'
        : 'bg-[#343541] text-gray-300 hover:bg-[#2d2f3a]'
    }`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-[#343541] rounded-lg p-8 max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Settings</h1>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex gap-4 mb-8">
          <button onClick={() => onNavigate('overview')} className={navButtonClass('overview')}>
            Overview
          </button>
          <button onClick={() => onNavigate('privacy')} className={navButtonClass('privacy')}>
            Privacy Policy
          </button>
          <button onClick={() => onNavigate('terms')} className={navButtonClass('terms')}>
            Terms of Service
          </button>
        </div>
        <div className="bg-[#23242a] rounded-2xl p-8 shadow-lg">
          {(!page || page === 'overview') && (
            <div>
              <h1 className="text-3xl font-bold mb-6 text-white">Overview</h1>
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-white">Usage Statistics</h2>
                  <div className="bg-[#343541] rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { label: 'Searches Made', key: 'searches_made' },
                        { label: 'Emails Fetched', key: 'emails_fetched' },
                        { label: 'Emails Drafted', key: 'emails_drafted' },
                        { label: 'Emails Sent', key: 'emails_sent' },
                      ].map(stat => (
                        <div key={stat.key} className="text-center">
                          <div className="text-2xl font-bold text-white">{usageStats[stat.key]}</div>
                          <div className="text-sm text-gray-300">{stat.label}</div>
                          <div className="text-xs text-gray-400">
                            {freemiumLimits[stat.key] - usageStats[stat.key]} remaining
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-semibold mb-4 text-white">Legal</h2>
                  <div className="space-y-2">
                    <p className="text-gray-300">
                      Review our{' '}
                      <button onClick={() => onNavigate('privacy')} className="text-white hover:underline">
                        Privacy Policy
                      </button>{' '}
                      and{' '}
                      <button onClick={() => onNavigate('terms')} className="text-white hover:underline">
                        Terms of Service
                      </button>{' '}
                      to understand how we protect your data and govern our service.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {page === 'privacy' && (
            <div className="prose prose-invert max-w-none">
              <ReactMarkdown components={markdownComponentsLarge}>{privacyContent}</ReactMarkdown>
            </div>
          )}
          {page === 'terms' && (
            <div className="prose prose-invert max-w-none">
              <ReactMarkdown components={markdownComponentsLarge}>{termsContent}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { SettingsModal };
